/**
 * @GestionalePD User Newsletter page UI
 */
import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import {
  Card,
  Container,
  Table,
  TableBody,
  TableContainer,
  TableFooter,
  TablePagination,
  TableCell,
  TableRow,
  styled,
  DialogTitle,
  DialogContent,
  DialogActions,
  Dialog,
  Typography,
  Box,
  Switch,
  Checkbox,
  useMediaQuery,
} from '@mui/material';
import { Unstable_Popup as BasePopup } from '@mui/base/Unstable_Popup';
import ControlPointOutlinedIcon from '@mui/icons-material/ControlPointOutlined';
//--------------------------------------------------------------------------
import { TablePaginationActions, theme } from '../../utils/TableStyles';
import { ErrorUI, HttpErrorHandler } from '../../utils/ErrorHandler';
import { LoadingUI } from '../../utils/LayoutStyles';
import API from '../../api';
import { entityTypeLabel } from '../../utils/EntityTypeLabel';
import {
  DefaultContainedButton,
  DefaultOutlinedButton,
  GreenOutlinedButton,
  RedOutlinedButton,
} from '../../utils/Buttons';
import {
  PopupSuccessBody,
  PopupErrorBody,
  POPUP_DURATION_SUCCESS,
  POPUP_DURATION_ERROR,
} from '../../utils/popUpStyles';

/**
 * @returns User Newsletter Page UI
 */

// popup custom style
const Popup = styled(BasePopup)`
  z-index: 1400;
`;

export default function NewsletterPage() {
  const [relationsList, setRelationsList] = useState([]);
  const [apiCalled, setApiCalled] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [currentProfile, setCurrentProfile] = useState('');
  const [canSave, setCanSave] = useState(false);

  const userData = JSON.parse(localStorage.getItem('userData'));

  useEffect(() => {
    setCheckedNews(userData.allow_news);
  }, [userData]);

  // Get relation lists
  const getRelationsList = () => {
    setErrorMessage('');

    API.get(`/userfollow/${userData.id}`)
      .then((response) => {
        setRelationsList(response.data);
        setApiCalled(true);
      })
      .catch((error) => {
        setApiCalled(true);
        HttpErrorHandler(error, setErrorMessage);
      });
  };

  // Initial relation loading
  useEffect(() => {
    getRelationsList();
  }, []);

  // get user data
  useEffect(() => {
    let data;

    if (localStorage.getItem('userData') !== 'undefined') data = JSON.parse(localStorage.getItem('userData'));

    if (data) {
      const _currentProfile = data.roles.filter((role) => role.last_used);
      setCurrentProfile(_currentProfile);
    }
  }, []);

  /* --------------------------- Delete Subscription -------------------------- */
  const [relationsToDelete, setRelationsToDelete] = useState([]);

  const handleAddRelationToDelete = (relation) => {
    setRelationsToDelete((prevState) =>
      prevState.includes(relation.entity.id)
        ? prevState.filter((id) => id !== relation.entity.id)
        : [...prevState, relation.entity.id]
    );

    setCanSave(true);
  };

  // Delete relation
  const onDeleteRelation = (e) => {
    e.preventDefault();

    const deleteRequests = relationsToDelete.map((relationId) => {
      const deleteData = {
        user_id: userData.id,
        entity_id: relationId,
      };

      return API.put(`/userfollow`, deleteData);
    });

    Promise.all(deleteRequests)
      .then((response) => {
        getRelationsList();
        setRelationsToDelete([]);
        setCanSave(false);
        showPopup(true, response[0].data.message);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  /* ------------------------------- Allow News ------------------------------- */
  const [checkedNews, setCheckedNews] = useState(false);
  const [openChangeCheckDialog, setOpenChangeCheckDialog] = useState(false);

  const handleOpenChangeCheckDialog = () => {
    setOpenChangeCheckDialog(true);
  };

  const handleCloseChangeCheckDialog = () => {
    setOpenChangeCheckDialog(false);
    setOpen(false);
  };

  const editAllowNews = (e) => {
    e.preventDefault();

    API.patch(`/users/${userData.id}/newsletters`, { allow_news: Number(!checkedNews) })
      .then((response) => {
        handleCloseChangeCheckDialog();
        setCheckedNews(!checkedNews);

        const data = JSON.parse(localStorage.getItem('userData')) || {};
        data.allow_news = Number(!checkedNews);
        localStorage.setItem('userData', JSON.stringify(data));
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  /* ---------------------------------- Popup --------------------------------- */
  const [anchor, setAnchor] = useState(null);
  const [open, setOpen] = useState(false);
  const [popUpText, setPopUpText] = useState('');
  const [popupSuccess, setPopupSuccess] = useState(true);

  // Show Popup message function
  const showPopup = (success, text) => {
    setPopupSuccess(success);
    setOpen(true);
    if (text) {
      setPopUpText(text);
    }

    setTimeout(
      () => {
        setOpen(false);
      },
      success ? POPUP_DURATION_SUCCESS : POPUP_DURATION_ERROR
    );
  };

  // popup component
  const displayPopup = () => {
    return (
      <Container ref={setAnchor} sx={{ position: 'absolute', bottom: '-20px' }}>
        <Popup anchor={anchor} open={open}  >
          {popupSuccess ? (
            <PopupSuccessBody sx={{ whiteSpace: 'pre-line' }}>{popUpText}</PopupSuccessBody>
          ) : (
            <PopupErrorBody>{popUpText}</PopupErrorBody>
          )}
        </Popup>
      </Container>
    );
  };

  /* ------------------------------- Pagination ------------------------------- */
  const [page, setPage] = useState(0);
  const is600Screen = useMediaQuery('(min-width: 600px)');
  const [rowsPerPage, setRowsPerPage] = useState(is600Screen ? 10 : 5);
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - relationsList.length) : 0;

  // Change Page
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Change Rows Per Page
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (currentProfile && currentProfile[0].prof_type_name !== 'USER') return <Navigate to={{ pathname: '/' }} />;

  return (
    <div>
      <Helmet>
        <title>Newsletter</title>
      </Helmet>

      <Container maxWidth="xl">
        <Card sx={{ padding: '24px', marginBottom: '24px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', marginBottom: '24px' }}>
            <Typography variant="h4">Preferenze comunicazioni</Typography>
            <Box>
              <Typography sx={{ lineHeight: '1' }}>
                Qui puoi controllare quali comunicazioni stai ricevendo, ed aggiornare le preferenze. Se vuoi seguire
                altri Circoli/Federazioni/Forum puoi seleionarli tramite la funzione{' '}
                <ControlPointOutlinedIcon sx={{ transform: 'translateY(25%)' }} /> dalla{' '}
                <Link to={'/user/organization'}>struttura organizzativa</Link>.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Typography sx={{ lineHeight: '1' }}>La mail utilizzata per le comunicazioni è:</Typography>
              <Typography sx={{ fontWeight: 'bold', lineHeight: '' }}>{userData.email}</Typography>
            </Box>
            <Box>
              <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <Typography sx={{ lineHeight: '1' }}>Stato Comunicazioni:</Typography>
                <Switch checked={!!checkedNews} onClick={handleOpenChangeCheckDialog} />
              </Box>
              <Typography sx={{ lineHeight: '1' }}>{`${
                checkedNews ? 'Disattiva' : 'Attiva'
              } tutte le comunicazioni dalle entità indicate qui sotto.`}</Typography>
            </Box>
          </Box>

          <Dialog open={openChangeCheckDialog} onClose={() => {}}>
            <DialogTitle>Modifica Stato Comunicazioni</DialogTitle>
            <DialogContent>
              {`Sei sicuro di voler ${checkedNews ? 'disattivare' : 'attivare'} tutte le comunicazioni?`}
            </DialogContent>
            <DialogActions>
              <DefaultOutlinedButton onClick={handleCloseChangeCheckDialog}>Chiudi</DefaultOutlinedButton>
              {checkedNews ? (
                <RedOutlinedButton onClick={editAllowNews}>Disattiva</RedOutlinedButton>
              ) : (
                <GreenOutlinedButton onClick={editAllowNews}>Attiva</GreenOutlinedButton>
              )}
            </DialogActions>
          </Dialog>
        </Card>

        {apiCalled ? (
          errorMessage.length === 0 ? (
            <Card sx={{ padding: '24px' }}>
              {/* Relations table UI */}
              <TableContainer>
                <Table size="small">
                  {relationsList.length > 0 ? (
                    <TableBody>
                      {(rowsPerPage > 0
                        ? relationsList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        : relationsList
                      ).map((relation) => (
                        <TableRow hover key={relation.id}>
                          <TableCell>{entityTypeLabel(relation.entity, true)}</TableCell>
                          <TableCell>{relation.entity.name}</TableCell>
                          <TableCell sx={{ width: '60px' }}>
                            <Checkbox
                              checked={!relationsToDelete.includes(relation.entity.id)}
                              onChange={() => handleAddRelationToDelete(relation)}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                      {emptyRows > 0 && (
                        <TableRow style={{ height: 55 * emptyRows }}>
                          <TableCell colSpan={6} />
                        </TableRow>
                      )}
                    </TableBody>
                  ) : (
                    <TableBody>
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          Nessuna entità disponibile
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}

                  {relationsList.length > rowsPerPage && (
                    // table pagination
                    <TableFooter>
                      <TableRow>
                        <ThemeProvider theme={theme}>
                          <TablePagination
                            sx={{ borderBottom: 'none' }}
                            rowsPerPageOptions={[]}
                            colSpan={4}
                            count={relationsList.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            labelRowsPerPage="Righe per Pagina"
                            ActionsComponent={TablePaginationActions}
                          />
                        </ThemeProvider>
                      </TableRow>
                    </TableFooter>
                  )}
                </Table>
              </TableContainer>
              <Box
                sx={{ display: relationsList.length > 0 ? 'flex' : 'none', justifyContent: 'end', marginTop: '24px' }}
              >
                <DefaultContainedButton onClick={onDeleteRelation} disabled={!canSave}>
                  Salva
                </DefaultContainedButton>
              </Box>
              {displayPopup()}
            </Card>
          ) : (
            ErrorUI(errorMessage)
          )
        ) : (
          LoadingUI()
        )}
      </Container>
    </div>
  );
}
